import { BETTING__SBK_VALIDATE_STAKE, BettingSportsbookValidateStake } from "@ppb/tbd-store/actions/betting";
import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { AlertProps, AlertType, AlertViewModel } from "@ppb/the-wall-common/types";
import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";
import { Dispatch } from "redux";
import { createOddsMovementSelector } from "../../store/state/betslip-module/betslip-odds-movement-selectors";
import { ExternalPushAction, EXTERNAL_PUSH } from "@ppb/tbd-store/actions/router";
import {
  BetslipSportsbookNotificationShownAction,
  BetslipSportsbookMaxPayoutNotificationUrlClickAction,
  UI__BETSLIP_SBK_NOTIFICATION_SHOWN,
  UI__BETSLIP_SBK_MAX_PAYOUT_NOTIFICATION_URL_CLICK,
  UI__BETSLIP_MAX_PAYOUT_NOTIFICATION_ACCEPTED,
  BetslipMaxPayoutNotificationAcceptedAction,
} from "@ppb/tbd-store/actions/betslip";
import { createValidationsSelector, NOTIFICATION_TYPES, SportsbookValidation } from "./notifier-mapper";
import {
  buildSportsbookTransactionalError,
  createAvailabilityNotificationSelector,
} from "../connected-sportsbook-betslip-mapper";
import { NotificationCode } from "../betslip-notification-code";
import { i18n } from "../../helpers/i18n";
import { createGetSbkIsDepositRequiredSelector } from "../betslip-mapper";
import { createIsConfirmStep } from "../sportsbook-betslip-confirm-mapper";
import { RUNNER_FAILURE_CODES } from "@ppb/betslip-core";
import { getAllUniqueRunnersFailures } from "../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import type {
  BetslipLocalization,
  SportsbookBetslipBalance,
  SportsbookBetslipCapabilities,
  SportsbookBetslipConfig,
} from "../../contract";
import {
  getSportsbookHandicapMovement,
  getSportsbookOddsMovement,
  getSportsbookConfirmationAvailability,
  getIsBetslipLoaded,
} from "../../store/state/betslip-module/betslip-module-selectors";
import { NavigationIntent } from "@ppb/tbd-store/clients/catalogue/catalogue-response-types";

export type StateProps = {
  notifications: AlertProps[];
  validations: (
    | SportsbookValidation
    | {
        type: NOTIFICATION_TYPES;
        notification: AlertProps;
      }
  )[];
};

export type ContainerProps = {
  className?: string;
  style?: Record<string, unknown>;
  isBetslipCollapsed?: boolean;
  localization: BetslipLocalization;
  config: SportsbookBetslipConfig;
  capabilities: SportsbookBetslipCapabilities;
  balance: SportsbookBetslipBalance;
};

function hasOnlyRequestedPriceUnavailableFailures(state: BetslipApplicationState): boolean {
  const runnerFailures = getAllUniqueRunnersFailures(state);
  return runnerFailures.every((failure) => failure === RUNNER_FAILURE_CODES.REQUESTED_PRICE_NOT_AVAILABLE);
}

export const makeMapStateToProps: MapStateToPropsFactory<
  StateProps | false,
  ContainerProps,
  BetslipApplicationState
> = () => {
  const oddsChangedNotification: AlertProps = {
    id: NotificationCode.OddsChanged,
    type: AlertType.Info,
    message: i18n({ key: "I18N.BETSLIP.ODDS_CHANGE_NOTIFICATION" }),
  };
  const handicapChangedNotification: AlertProps = {
    id: NotificationCode.HandicapChanged,
    type: AlertType.Info,
    message: i18n({ key: "I18N.BETSLIP.HANDICAP_CHANGE_NOTIFICATION" }),
  };
  const oddsAndHandicapChangedNotification: AlertProps = {
    id: NotificationCode.OddsHandicap,
    type: AlertType.Info,
    message: i18n({ key: "I18N.BETSLIP.ODDS_HANDICAP_CHANGE_NOTIFICATION" }),
  };
  const oddsAndAvailabilityNotification: AlertProps = {
    id: NotificationCode.OddsAvailability,
    type: AlertType.Warning,
    message: i18n({ key: "I18N.BETSLIP.ODDS_AVAILABILITY_NOTIFICATION" }),
  };
  const handicapAndAvailabilityNotification: AlertProps = {
    id: NotificationCode.HandicapAvailability,
    type: AlertType.Warning,
    message: i18n({ key: "I18N.BETSLIP.AVAILABILITY_HANDICAP_NOTIFICATION" }),
  };
  const oddsAndHandicapAndAvailabilityNotification: AlertProps = {
    id: NotificationCode.OddsHandicapAvailability,
    type: AlertType.Warning,
    message: i18n({ key: "I18N.BETSLIP.ODDS_AVAILABILITY_HANDICAP_NOTIFICATION" }),
  };
  const depositPromptInsufficientFundsNotification: AlertProps = {
    id: NotificationCode.InsufficientFunds,
    message: i18n({ key: "I18N.BETSLIP.ERROR.FUNDS_REQUIRED" }),
    detail: i18n({ key: "I18N.BETSLIP.ERROR.PLEASE_DEPOSIT" }),
    type: AlertType.Info,
    gtmLabel: `${AlertType.Error}I18N.BETSLIP.ERROR.FUNDS_REQUIRED`,
  };
  const insufficientFundsNotification: AlertProps = {
    id: NotificationCode.InsufficientFunds,
    message: i18n({ key: "I18N.BETSLIP.SBK.ERROR.INSUFFICIENT_FUNDS" }),
    type: AlertType.Error,
    gtmLabel: `${AlertType.Error}I18N.BETSLIP.SBK.ERROR.INSUFFICIENT_FUNDS`,
  };

  const getIsConfirmStep = createIsConfirmStep();
  const getValidations = createValidationsSelector();
  const getAvailability = createAvailabilityNotificationSelector();
  const getOddsMovementState = createOddsMovementSelector();
  const getIsDepositRequired = createGetSbkIsDepositRequiredSelector();

  return (state: BetslipApplicationState, { localization, config, capabilities, balance }: ContainerProps) => {
    if (!getIsBetslipLoaded(state)) {
      return false;
    }

    const insufficientFunds = capabilities.depositPrompt.enabled
      ? depositPromptInsufficientFundsNotification
      : insufficientFundsNotification;

    const isConfirmStep = getIsConfirmStep(state);
    const validations = getValidations(state, {
      localization,
      maxPayoutTermsUrl: config.termsUrls.maxPayout,
      dailyPayoutLimit: capabilities.dailyPayoutLimit,
    });
    const availability = isConfirmStep ? false : getAvailability(state, capabilities);
    const oddsMovementMap = getOddsMovementState(getSportsbookOddsMovement(state));
    const isDepositRequired = getIsDepositRequired(state, balance);
    const sportsbookHandicapMovement = getSportsbookHandicapMovement(state);
    const hasAnyOddChanged = Object.values(oddsMovementMap).some((each) => each.movement !== null);
    const availabilityChangedDuringConfirmStep = getSportsbookConfirmationAvailability(state);
    const validationsNotifications = validations
      .map(({ notification }) => notification)
      .filter((notification): notification is AlertViewModel => !!notification);

    // Validation notifications have higher precedence
    const highPriorityNotifications = (validationsNotifications.length > 0 && validationsNotifications) || null;

    const hasAnyHandicapChanged = sportsbookHandicapMovement
      ? Object.values(sportsbookHandicapMovement).some((each) => each.hasHandicapChanged)
      : false;

    // if we have new odds movement alert experience
    // we want to filter out the requested price not available failure
    // as it will be surfaced as odds movement alert instead of transactional error
    const shouldSuppressError =
      capabilities.acceptOddsMovementAlert.enabled && hasOnlyRequestedPriceUnavailableFailures(state);
    const transactionalError = shouldSuppressError ? undefined : buildSportsbookTransactionalError(state);

    // order matters
    const notifications = !capabilities.oddsChangeNotification.enabled
      ? [
          {
            condition: !!availability && hasAnyOddChanged && hasAnyHandicapChanged,
            item: [oddsAndHandicapAndAvailabilityNotification],
          },
          { condition: hasAnyOddChanged && hasAnyHandicapChanged, item: [oddsAndHandicapChangedNotification] },
          { condition: availabilityChangedDuringConfirmStep, item: [oddsAndAvailabilityNotification] },
          { condition: hasAnyHandicapChanged && !!availability, item: [handicapAndAvailabilityNotification] },
          { condition: !!availability, item: availability ? [availability] : [] },
          { condition: hasAnyHandicapChanged, item: [handicapChangedNotification] },
        ]
      : [
          {
            condition: !!availability && hasAnyOddChanged && hasAnyHandicapChanged,
            item: [oddsAndHandicapAndAvailabilityNotification],
          },
          { condition: hasAnyOddChanged && hasAnyHandicapChanged, item: [oddsAndHandicapChangedNotification] },
          { condition: hasAnyOddChanged && !!availability, item: [oddsAndAvailabilityNotification] },
          { condition: availabilityChangedDuringConfirmStep, item: [oddsAndAvailabilityNotification] },
          { condition: hasAnyHandicapChanged && !!availability, item: [handicapAndAvailabilityNotification] },
          { condition: !!availability, item: availability ? [availability] : [] },
          { condition: hasAnyOddChanged, item: [oddsChangedNotification] },
          { condition: hasAnyHandicapChanged, item: [handicapChangedNotification] },
        ];
    const lowPriorityNotification = notifications.find((notification) => notification.condition);
    const lowPriorityNotifications = lowPriorityNotification ? [...lowPriorityNotification.item] : [];

    const transactionalNotification = transactionalError ? [transactionalError] : [];
    const generalNotifications = highPriorityNotifications || lowPriorityNotifications;

    if (isDepositRequired) {
      return {
        notifications: [insufficientFunds, ...generalNotifications],
        validations,
      };
    }

    return {
      notifications: [...transactionalNotification, ...generalNotifications],
      validations,
    };
  };
};

export type DispatchActions =
  | BettingSportsbookValidateStake
  | BetslipSportsbookNotificationShownAction
  | BetslipSportsbookMaxPayoutNotificationUrlClickAction
  | ExternalPushAction
  | BetslipMaxPayoutNotificationAcceptedAction;

export type DispatchProps = {
  dispatchSportsbookValidateStake: (combinationId: string) => void;
  dispatchSportsbookNotificationShown: (gtmLabel: string) => void;
  dispatchSportsbookMaxPayoutNotificationUrlClick: (url: string) => void;
  dispatchExternalPush: (url: string) => void;
  dispatchMaxPayoutNotificationAccepted: () => void;
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (
  dispatch: Dispatch<DispatchActions>,
) => ({
  dispatchSportsbookValidateStake: (combinationId) => {
    dispatch<BettingSportsbookValidateStake>({
      type: BETTING__SBK_VALIDATE_STAKE,
      payload: { combinationId },
    });
  },

  dispatchSportsbookNotificationShown: (label) => {
    dispatch<BetslipSportsbookNotificationShownAction>({
      type: UI__BETSLIP_SBK_NOTIFICATION_SHOWN,
      payload: {
        label,
      },
    });
  },

  dispatchSportsbookMaxPayoutNotificationUrlClick: (url) => {
    dispatch<BetslipSportsbookMaxPayoutNotificationUrlClickAction>({
      type: UI__BETSLIP_SBK_MAX_PAYOUT_NOTIFICATION_URL_CLICK,
      payload: {
        url,
      },
    });
  },

  dispatchExternalPush: (url: string) => {
    dispatch<ExternalPushAction>({
      type: EXTERNAL_PUSH,
      payload: {
        viewUrn: "",
        viewUrl: url,
        gtmData: {
          label: "I18N.BETSLIP.VALIDATION_SEE_TC_FOR_MORE_INFO",
          moduleName: "betslip",
        },
        navigationIntent: NavigationIntent.External,
      },
    });
  },

  dispatchMaxPayoutNotificationAccepted: () => {
    dispatch<BetslipMaxPayoutNotificationAcceptedAction>({
      type: UI__BETSLIP_MAX_PAYOUT_NOTIFICATION_ACCEPTED,
    });
  },
});
